import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { StyleSheet } from 'react-native-unistyles';
import { supabase } from '@/data/client';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import AutomationBindingInput from './AutomationBindingInput';
import { isBinding, filterVariablesForParam } from '@/utils/automationBuilder';

/**
 * PeopleTargetInput — explicit people targets for v2 actions (§14.5).
 * The user picks ONE source: specific people OR a bound variable — never
 * both. Variable mode accepts a person/people variable (a people list or
 * a single person record); literal mode uses the multi people picker.
 *
 * Value shape is the worker contract: an array of person literals
 * (uuid strings or {id,...} picker objects) OR a single-element array
 * holding one {var:key} binding. The caller normalizes via build*Config.
 */
const PeopleTargetInput = ({
    variables = [],
    value,
    onUpdate,
    title = 'People',
    required,
    multiple = true,
    testID,
    fromScreen,
    fromStoreKey,
    helpText,
}) => {
    const personVars = useMemo(
        () => filterVariablesForParam(variables, multiple ? { semantic: 'peopleList', multiple: true } : { semantic: 'personID' }),
        [variables, multiple]);

    const entries = useMemo(() => {
        if (Array.isArray(value)) return value.filter(v => v !== undefined && v !== null);
        return value === undefined || value === null ? [] : [value];
    }, [value]);

    const bindings = entries.filter(isBinding);
    const [mode, setMode] = useState(bindings.length ? 'variable' : 'people');
    const isVariable = mode === 'variable';

    const literalIds = useMemo(
        () => entries.filter(v => typeof v === 'string' && /^[0-9a-f-]{36}$/i.test(v)),
        [entries]);
    const literalPeople = useMemo(
        () => entries.filter(v => v && typeof v === 'object' && !isBinding(v) && v.id),
        [entries]);

    // Saved configs persist uuids — rehydrate them for the people picker.
    const { data: hydrated } = useQuery({
        queryKey: ['peopleTargetHydration', literalIds.slice().sort().join(',')],
        queryFn: async () => {
            const { data, error } = await supabase.from('People')
                .select('id, firstName, lastName, avatarUpdatedAt, avatarStorageKey')
                .in('id', literalIds);
            if (error) throw error;
            return data || [];
        },
        enabled: literalIds.length > 0,
        staleTime: 1000 * 60 * 5,
    });

    const peopleForPicker = useMemo(() => [...literalPeople, ...(hydrated || [])], [literalPeople, hydrated]);

    const switchMode = (next) => {
        if (next === mode) return;
        setMode(next);
        // Modes are exclusive — switching clears the other side's entries.
        if (next === 'variable' && literalIds.length + literalPeople.length) {
            onUpdate(multiple ? bindings : bindings[0]);
        } else if (next === 'people' && bindings.length) {
            const literals = [...literalIds, ...literalPeople];
            onUpdate(multiple ? literals : literals[0]);
        }
    };

    const literalEntries = isVariable ? [] : [...literalIds, ...literalPeople];
    return (
        <View>
            <View style={styles.modeRow}>
                <TouchableOpacity
                    style={styles.modeBtn(!isVariable)}
                    onPress={() => switchMode('people')}
                    testID={`${testID || 'people-target'}-mode-people`}
                >
                    <Text style={styles.modeBtnText(!isVariable)}>People</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={styles.modeBtn(isVariable)}
                    onPress={() => switchMode('variable')}
                    testID={`${testID || 'people-target'}-mode-variable`}
                >
                    <Text style={styles.modeBtnText(isVariable)}>Trigger variable</Text>
                </TouchableOpacity>
            </View>

            {isVariable ? (
                <AutomationBindingInput
                    title={title}
                    placeholder={multiple ? 'Bind a person or people variable...' : 'Bind a person variable...'}
                    variables={personVars}
                    value={bindings[0]}
                    onUpdate={(val) => onUpdate(multiple ? (val ? [val] : []) : val)}
                    required={required}
                    allowManualEntry={false}
                    helpText={helpText}
                    testID={testID}
                />
            ) : (
                <SheetSelector
                    type="people"
                    title={title}
                    placeholder={multiple ? 'Select people' : 'Select a person'}
                    multiple={multiple}
                    value={multiple ? peopleForPicker : peopleForPicker[0] || null}
                    onChange={(people) => {
                        if (multiple) {
                            onUpdate((people || []).map(p => p?.id ? p.id : p).filter(Boolean));
                        } else {
                            onUpdate(people?.id ? [people.id] : []);
                        }
                    }}
                    required={required}
                    fromScreen={fromScreen}
                    fromStoreKey={fromStoreKey}
                />
            )}
            {!isVariable && helpText ? <Text style={styles.help}>{helpText}</Text> : null}
        </View>
    );
};

const styles = StyleSheet.create((theme) => ({
    modeRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },
    modeBtn: (active) => ({
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: active ? theme.colors.primaryContainer : theme.colors.surfaceVariant,
    }),
    modeBtnText: (active) => ({
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: active ? theme.colors.primary : theme.colors.textSecondary,
    }),
    help: { fontSize: 12, color: theme.colors.textSecondary, marginTop: -4, marginBottom: 8 },
}));

export default PeopleTargetInput;
